import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { Sparkles, ExternalLink, ShieldCheck, Ban } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * NewInnovationTopicsSection — Phase 30.3.
 * Renders the 10 new innovation topics + their 5 anchor sources each,
 * plus the ALLOWED / BLOCKED policy panels so visitors understand
 * exactly what the platform will process.
 */
export default function NewInnovationTopicsSection() {
  const { data: topicsData } = useQuery({
    queryKey: ["knowledge-new-topics"],
    queryFn: () => axios.get(`${API}/knowledge/topics/new`).then((r) => r.data),
  });
  const { data: policy } = useQuery({
    queryKey: ["knowledge-innovation-policy"],
    queryFn: () => axios.get(`${API}/knowledge/innovation/policy`).then((r) => r.data),
  });

  const topics = topicsData?.topics || [];

  return (
    <section
      id="new-innovation-topics"
      data-testid="landing-new-topics"
      className="relative z-10 max-w-7xl mx-auto px-6 pb-16"
    >
      <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
        <div>
          <span className="text-[10px] uppercase tracking-[0.2em] font-mono text-cyan-300 flex items-center gap-2">
            <Sparkles size={12} /> INNOVATION LAYER · 10 NEW TOPICS
          </span>
          <h2 className="font-mono text-2xl sm:text-3xl tracking-tight mt-1">
            10 fresh research fronts.{" "}
            <span className="text-cyan-300">50 anchor sources.</span>{" "}
            <span className="text-emerald-300">50 items/day</span>, library
            capped at 500.
          </h2>
          <p className="mt-1 text-[12.5px] text-zinc-400 max-w-3xl">
            These topics are additive to the existing 20 evergreen libraries.
            Every entry is anchored to five whitelisted sources so the trust
            ranker can score it before it is ever promoted into your
            permanent library.
          </p>
        </div>
      </div>

      {/* Policy strip — ALLOWED / BLOCKED */}
      {policy && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
          <div className="border border-emerald-500/40 bg-emerald-500/[0.04] p-4">
            <div className="flex items-center gap-2 text-emerald-300 mb-2 text-[10px] font-mono uppercase tracking-[0.2em]">
              <ShieldCheck size={12} /> ALLOWED
            </div>
            <div className="flex flex-wrap gap-1.5">
              {policy.allowed_topics_examples.map((a) => (
                <span
                  key={a}
                  className="text-[11px] font-mono border border-emerald-500/30 bg-[#0A0A0A] px-2 py-0.5 text-emerald-200"
                >
                  {a}
                </span>
              ))}
            </div>
          </div>
          <div className="border border-rose-500/40 bg-rose-500/[0.04] p-4">
            <div className="flex items-center gap-2 text-rose-300 mb-2 text-[10px] font-mono uppercase tracking-[0.2em]">
              <Ban size={12} /> BLOCKED
            </div>
            <div className="flex flex-wrap gap-1.5">
              {policy.blocked_topics_examples.map((b) => (
                <span
                  key={b}
                  className="text-[11px] font-mono border border-rose-500/30 bg-[#0A0A0A] px-2 py-0.5 text-rose-200"
                >
                  {b}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Topic grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {topics.map((t) => (
          <div
            key={t.slug}
            data-testid={`new-topic-card-${t.slug}`}
            className="border border-cyan-500/30 bg-[#0A0A0A] p-4"
          >
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-cyan-300">
              {t.category}
            </div>
            <div className="mt-1 font-mono text-white text-[14.5px] leading-tight">
              {t.name}
            </div>
            <p className="mt-1 text-[11.5px] text-zinc-400 leading-relaxed">
              {t.why}
            </p>
            <div className="mt-3 border-t border-[#27272A] pt-2">
              <div className="text-[9px] uppercase tracking-[0.2em] font-mono text-zinc-500 mb-1">
                TOP 5 SOURCES
              </div>
              <ul className="space-y-0.5 text-[11px] font-mono">
                {(t.top_sources || []).map((s) => (
                  <li key={s.key} className="flex items-center gap-1.5">
                    <span
                      className={
                        s.category === "university"
                          ? "text-purple-300"
                          : "text-cyan-300"
                      }
                    >
                      ·
                    </span>
                    <a
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-zinc-300 hover:text-white truncate"
                    >
                      {s.name}
                    </a>
                    <ExternalLink
                      size={9}
                      className="text-zinc-600 flex-shrink-0"
                    />
                    <span className="ml-auto text-[9px] text-zinc-500 tabnum">
                      {s.credibility.toFixed(2)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
